import { isSameDay, toCalendarDate, today } from "@internationalized/date"
import {
  sanitizeDateValue,
  fromInternationalizedDateToLuxonDate,
  fromLuxonDateToInternationalizedDate,
} from "./utils"
import { ReactAriaCalendarProps, DatePickerProps } from "./types"

interface GetAriaCalendarPropsOptions
  extends Pick<
    DatePickerProps,
    | "value"
    | "defaultValue"
    | "minValue"
    | "maxValue"
    | "disablePastDates"
    | "disableFutureDates"
    | "disableSpecificDates"
    | "isDateDisabled"
    | "onChange"
  > {
  timeZone: string
}

/**
 * This function computes, with help of utils functions which converts Luxon date
 * to Internationalized date and vice versa, and returns the props that are needed by React Aria calendar.
 */
export function getAriaCalendarProps({
  value,
  defaultValue,
  minValue,
  maxValue,
  timeZone,
  disablePastDates,
  disableFutureDates,
  disableSpecificDates,
  isDateDisabled,
  onChange,
}: GetAriaCalendarPropsOptions): ReactAriaCalendarProps {
  const _today = today(timeZone)
  const didUserDisableSpecificDates = disableSpecificDates && disableSpecificDates.length > 0

  return {
    value: sanitizeDateValue(timeZone, value),
    defaultValue: sanitizeDateValue(timeZone, defaultValue),
    onChange(value) {
      if (!value) return onChange?.(null)
      return onChange?.(fromInternationalizedDateToLuxonDate(value, timeZone))
    },
    isDateUnavailable(date) {
      if (date === null) {
        return false
      }
      let isDateUnavailable = false

      if (minValue && minValue.isValid && !isDateUnavailable) {
        const _minValue = fromLuxonDateToInternationalizedDate(minValue, timeZone)
        isDateUnavailable = date.compare(_minValue) < 0
      }

      if (maxValue && maxValue.isValid && !isDateUnavailable) {
        const _maxValue = fromLuxonDateToInternationalizedDate(maxValue, timeZone)
        isDateUnavailable = date.compare(_maxValue) > 0
      }

      if (disablePastDates && !isDateUnavailable) {
        isDateUnavailable = date.compare(_today) < 0
      }

      if (disableFutureDates && !isDateUnavailable) {
        isDateUnavailable = date.compare(_today) > 0
      }

      if (didUserDisableSpecificDates && !isDateUnavailable) {
        isDateUnavailable = !!disableSpecificDates?.find(dateToDisable => {
          return isSameDay(date, fromLuxonDateToInternationalizedDate(dateToDisable, timeZone))
        })
      }

      if (isDateDisabled && typeof isDateDisabled === "function" && !isDateUnavailable) {
        const _date = toCalendarDate(date)
        isDateUnavailable = isDateDisabled(fromInternationalizedDateToLuxonDate(_date, timeZone))
      }

      return isDateUnavailable
    },
  }
}
